import React from 'react'
import { ArrowUpDown, Grid, List, X } from 'lucide-react'

import { SkeletonLoadingHistory } from 'src/features/SkeletonLoadingHistory'
import { SheetForMaintenanceHistory } from 'src/features/SheetForMaintenanceHistory'
import { MaintenanceHistoryCard } from 'src/features/MaintenanceHistoryCard'
import { ButtonNavigateToCars } from 'src/features/ButtonNavigateToCars'
import { TooltipWrapper } from 'src/components/Tooltip'
import Title from 'src/components/Title'
import Container from 'src/components/Container'
import AppBreadcrumb from 'src/components/AppBreadcrumb'

import { useMaintenanceHistory } from 'src/pages/ServiceMaintenanceHistory/useMaintenanceHistory'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/shadcn/select'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'src/components/shadcn/pagination'
import { Input } from 'src/components/shadcn/input'
import { Button } from 'src/components/shadcn/button'
import { Badge } from 'src/components/shadcn/badge'

const ServiceMaintenanceHistory = (): JSX.Element => {
  const {
    locale,
    breadcrumbItems,
    isLoadingGetAllMaintenanceHistory,
    allMaintenanceHistory,
    listOfSorts,
    searchText,
    handleSearchChange,
    sortState,
    handleChangeSortState,
    isReversed,
    toggleReversed,
    handlePreviousPage,
    handleNextPage,
    handleSetPage,
    currentPage,
    arrPages,
    isList,
    toggleListDisplay,
    clearInput,
    setFilters,
    filters,
    filtersCount,
    inputRef,
    currentCar,
    numberOfFoundItems,
    hiddenOnMobile,
  } = useMaintenanceHistory()

  return (
    <Container>
      <AppBreadcrumb items={breadcrumbItems} />
      <div className="flex justify-between gap-3 mt-5">
        <div className="relative w-full">
          <Input
            ref={inputRef}
            placeholder={locale.inputPlaceholder}
            className="rounded-[10px]"
            value={searchText}
            onChange={handleSearchChange}
          />
          {searchText.length > 0 && (
            <div className="absolute top-[5px] right-1 flex flex-row gap-1 items-center">
              <Badge className="rounded-[15px] select-none h-5 w-5 text-[12px] flex items-center justify-center">
                {numberOfFoundItems}
              </Badge>
              <Button
                variant="ghost"
                className="rounded-[15px] w-8 h-8"
                onClick={clearInput}
              >
                <X />
              </Button>
            </div>
          )}
        </div>
        <Select value={sortState} onValueChange={handleChangeSortState}>
          <SelectTrigger className="lg:w-1/4 sm:w-1/2 w-full rounded-[10px]">
            <SelectValue placeholder={locale.sortStates.byOdometer} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {listOfSorts.map(({ type, title }) => (
                <SelectItem key={type} value={type}>
                  {title}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div className="mt-5 flex justify-between gap-3">
        <div className="flex items-center">
          <h2 className={`${hiddenOnMobile} font-bold`}>{currentCar}:</h2>
          <ButtonNavigateToCars />
        </div>
        <div className="flex items-center gap-3">
          <TooltipWrapper text={locale.tooltips.list}>
            <Button
              variant="outline"
              className="rounded-[10px]"
              onClick={toggleListDisplay}
            >
              {isList ? <List /> : <Grid />}
            </Button>
          </TooltipWrapper>
          <TooltipWrapper text={locale.tooltips.reverse}>
            <Button
              variant="outline"
              className={`rounded-[10px] ${isReversed ? 'bg-accent' : ''}`}
              onClick={toggleReversed}
            >
              <ArrowUpDown />
            </Button>
          </TooltipWrapper>
          <SheetForMaintenanceHistory
            filters={filters}
            setFilters={setFilters}
            filtersCount={filtersCount}
          />
        </div>
      </div>
      <div className="mt-5">
        {isLoadingGetAllMaintenanceHistory ? (
          <SkeletonLoadingHistory isList countOfFields={4} />
        ) : allMaintenanceHistory.length > 0 ? (
          <div>
            <div
              className={
                isList ? '' : 'grid md:grid-cols-2 lg:grid-cols-3 gap-4'
              }
            >
              {allMaintenanceHistory.map(maintenance => {
                return (
                  <div key={maintenance.id} className={isList ? 'mt-3' : ''}>
                    <MaintenanceHistoryCard {...maintenance} />
                  </div>
                )
              })}
            </div>
            <div>
              <Pagination className="my-5">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={handlePreviousPage}
                      className="rounded-[10px] select-none"
                    />
                  </PaginationItem>
                  {arrPages.map(page => {
                    return (
                      <PaginationItem key={crypto.randomUUID()}>
                        <PaginationLink
                          isActive={page === currentPage}
                          onClick={() => handleSetPage(page)}
                          className="rounded-[10px]"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  })}
                  <PaginationItem>
                    <PaginationNext
                      onClick={handleNextPage}
                      className="rounded-[10px] select-none"
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          </div>
        ) : (
          <Title
            title={locale.noResult}
            classes="text-center text-xl font-bold mt-8"
          />
        )}
      </div>
    </Container>
  )
}

export default ServiceMaintenanceHistory
